import { useState } from 'react';
import type React from 'react';
import classNames from 'classnames';

import Loader from 'components/partials/loader/loader';
import Button from 'components/partials/button/button';
import Search from 'components/partials/search/search';
import { useFeatureHintTarget } from 'components/shared/feature-hints';
import SessionItem from './session-item';
import TokenUsageIndicator from './token-usage-indicator';

import { useFeatureFlags } from 'components/shared/feature-flags';

import { useCurrentUser } from 'hooks/api/use-current-user';

import { useApiGetConversations } from '../../hooks/use-api-get-conversations';
import { useTokenUsage } from '../../hooks/use-token-usage';

import {
  getFallbackResetDate,
  getIsTokenUsageExempt,
} from '../../helpers/token-usage-helpers';

import { GUIDE_FAQ_URL } from '../../ai-assistant.constants';

import Icons from 'assets/icons';

interface Session {
  id: string;
  name: string;
  createdAt: string;
}

interface SessionsSidebarProps {
  isOpen: boolean;
  onToggle: () => void;
  conversationId: string | null;
  setSearchParams: (params: Record<string, string>) => void;
  clientId: string | number;
  onFocusMessageInput?: () => void;
  isReadOnly?: boolean;
}

const SessionsSidebar = ({
  isOpen,
  onToggle,
  conversationId,
  setSearchParams,
  clientId,
  onFocusMessageInput,
  isReadOnly,
}: SessionsSidebarProps): React.JSX.Element => {
  const { featureFlags } = useFeatureFlags();
  const isTokenUsageEnabled = featureFlags['ai-token-usage'];
  const currentUser = useCurrentUser();
  const [searchTerm, setSearchTerm] = useState('');
  const isAdmin = currentUser?.data?.fdUser || currentUser?.data?.clientAdmin;
  const isTokenUsageExempt = getIsTokenUsageExempt(currentUser?.data);
  const hintTargetProps = useFeatureHintTarget('new-chat');
  const usageHintTargetProps = useFeatureHintTarget('token-usage');
  const { percentRemaining, resetDate, hasUsageData, isError, isLoading } =
    useTokenUsage({
      oktaUserId: currentUser?.data?.oktaUserId ?? '',
      enabled: !isTokenUsageExempt,
    });

  const { data: conversations = [], isLoading: isLoadingConversations } =
    useApiGetConversations({
      oktaUserId: currentUser?.data?.oktaUserId as string,
      clientId,
      searchTerm,
    });

  const conversationsArray = conversations as Session[];
  const hasConversations = conversationsArray.length > 0;

  const handleNewChat = (): void => {
    setSearchParams({});
    onFocusMessageInput?.();
  };

  return (
    <div
      className={classNames(
        'absolute top-0 left-0 z-10 flex flex-col gap-4 p-4',
        isOpen ? 'h-full border-r w-64' : 'h-11 border-r-0 w-40'
      )}
    >
      <div
        className={classNames(
          'flex items-center text-navy-800 transition-[gap] duration-300 ease-out',
          isOpen ? 'justify-between gap-2' : 'justify-start gap-1'
        )}
      >
        <Button
          variant="reserve-text"
          className="!size-10 !p-0"
          onClick={onToggle}
          aria-label="Toggle AI Assistant sidebar"
        >
          <Icons.AiSidebarLeft customClass="w-5 h-5" />
        </Button>

        <Button
          {...hintTargetProps}
          variant="reserve-text"
          className={classNames(hintTargetProps.className)}
          iconBefore={<Icons.AiChatNew />}
          onClick={handleNewChat}
          disabled={isReadOnly}
        >
          {isOpen ? 'New chat' : ''}
        </Button>
      </div>

      <div
        className={classNames(
          'grid min-h-0 flex-1 transition-[grid-template-rows,opacity] duration-300 ease-out',
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        )}
      >
        <div className="flex min-h-0 flex-col min-w-0 gap-4">
          <div className="flex min-h-0 flex-1 flex-col gap-4 pt-2">
            <Search
              placeholder="Search"
              customClassIcon="text-gray-500"
              setSearch={setSearchTerm}
            />

            <div className="flex min-h-0 flex-1 flex-col">
              <h4 className="shrink-0 font-semibold text-gray-700 px-2">
                Session history
              </h4>

              <div className="relative overflow-y-auto h-full scrollbar-thin">
                {hasConversations && !isLoadingConversations ? (
                  <div className="flex flex-col gap-1 min-h-0 flex-1">
                    {conversationsArray.map((session) => (
                      <SessionItem
                        key={session.id}
                        session={session}
                        isActive={session.id === conversationId}
                        setSearchParams={setSearchParams}
                        clientId={clientId}
                        isReadOnly={isReadOnly}
                      />
                    ))}
                  </div>
                ) : isLoadingConversations ? (
                  <Loader
                    loading
                    fullScreen={false}
                    customClass="!bg-white"
                    overlay="white"
                  />
                ) : (
                  <div className="p-2">
                    <p className="text-sm text-gray-600 font-medium">
                      No chats yet
                    </p>
                    <p className="text-sm text-gray-500 flex items-center gap-0.5 flex-wrap">
                      Start a
                      <Button
                        variant="reserve-text-link"
                        size="xs"
                        className="p-0"
                        onClick={handleNewChat}
                        disabled={isReadOnly}
                      >
                        new chat
                      </Button>
                      .
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {!isTokenUsageExempt &&
            (!isTokenUsageEnabled || isLoading || hasUsageData || isError) && (
              <div
                {...usageHintTargetProps}
                className={classNames(
                  'shrink-0',
                  usageHintTargetProps.className
                )}
              >
                <TokenUsageIndicator
                  percentRemaining={
                    isTokenUsageEnabled ? percentRemaining : 100
                  }
                  resetDate={
                    isTokenUsageEnabled ? resetDate : getFallbackResetDate()
                  }
                  hasError={isTokenUsageEnabled ? isError : false}
                  isLoading={isTokenUsageEnabled ? isLoading : false}
                />
              </div>
            )}

          {!isAdmin && (
            <Button
              variant="reserve-outlined"
              iconBefore={<Icons.OpenBook />}
              className="shrink-0 w-full"
              onClick={() => window.open(GUIDE_FAQ_URL, '_blank')}
            >
              Guide & FAQ
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionsSidebar;
